/**
 * Vocabulário de tela do redirecionador — o que cada estado SIGNIFICA para quem opera.
 *
 * Separado da tela pelo mesmo motivo de `campaign-ui.ts`: o teste afirma sobre os rótulos sem
 * montar a página, e lista, gaveta e diagnóstico usam o mesmo texto.
 *
 * Nada aqui decide regra de negócio e **nada aqui calcula métrica**. O servidor manda
 * `clicksTotal`, `clicksNoOrigin` e `lastClickAt` contados no banco; o que esta camada faz é
 * CLASSIFICAR (comparar dois números que já vieram prontos) e TRADUZIR. Não há soma, não há
 * divisão e — de propósito — **não há porcentagem**: dois caminhos para o mesmo número é como
 * um deles fica errado sem ninguém notar, e o número de porcentagem por origem é da Fase 8.
 */
import type { Redirector, RedirectorClick } from '../../lib/api';

/* ========================================================================== */
/*  Onde o link mora                                                           */
/* ========================================================================== */

/**
 * ⚠️ **A FONTE DO ENDEREÇO É O SERVIDOR** (Fase 6).
 *
 * `redirector.publicUrl` vem pronto de `redirector.list`/`get`: quem conhece
 * `REDIRECT_BASE_URL` é o processo que serve o redirecionador. Use `enderecoPublico()`.
 *
 * O que está abaixo (`redirectBase`/`shortLinkFor`) continua existindo como **rede de
 * segurança** para um painel que rode contra uma api antiga, e é usado por `enderecoPublico`
 * só quando o servidor não mandou nada. Não é um segundo caminho concorrente: é o degrau de
 * baixo do mesmo caminho, e está escrito num lugar só.
 */

/**
 * A base pública do redirecionador (`https://r.dominio.com`), que em produção é um HOST
 * PRÓPRIO — nem o do painel, nem o da api.
 *
 * Por que isto pode ser `null` em vez de um palpite: o endereço vai para dentro de um anúncio
 * pago. Um link inventado (`window.location.origin`, por exemplo) seria copiado, colado num
 * criativo e só apareceria como erro depois do dinheiro gasto. Não saber e DIZER que não sabe
 * é a única saída honesta — a tela mostra o aviso e o slug, e ninguém copia um link quebrado.
 */
export function redirectBase(): string | null {
  const raw = import.meta.env.VITE_REDIRECT_BASE_URL;
  return typeof raw === 'string' ? normalizarBase(raw) : null;
}

/**
 * O endereço público completo. `null` quando a base não foi configurada neste painel.
 *
 * A normalização da barra final mora AQUI, e não só em `redirectBase()`, porque este é o
 * ponto por onde todo endereço passa — inclusive o do teste, que passa a base na mão.
 * Normalizar num lugar e montar em outro é como `https://r.dominio//r/abc` sai para dentro
 * de um anúncio sem ninguém ver.
 */
export function shortLinkFor(slug: string, base: string | null = redirectBase()): string | null {
  const limpa = base === null ? null : normalizarBase(base);
  return limpa === null ? null : `${limpa}/r/${slug}`;
}

function normalizarBase(raw: string): string | null {
  const trimmed = raw.trim().replace(/\/+$/, '');
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * O endereço público de um link — **o que a tela mostra e o que a pessoa copia**.
 *
 * Prefere sempre o `publicUrl` do servidor. Antes da Fase 6 a tela montava este endereço
 * sozinha, a partir de `VITE_REDIRECT_BASE_URL`: dois lugares sabendo o mesmo endereço, e
 * esse endereço vai para dentro de um anúncio pago. No dia em que o host mudasse, um dos dois
 * continuaria entregando o antigo — e ninguém descobriria pelo erro, e sim pelo clique que
 * parou de chegar.
 *
 * **Devolve `null`, e não um palpite.** Se nem o servidor mandou endereço nem há base
 * configurada no painel, a resposta honesta é "não sei" — a tela mostra o aviso e esconde o
 * botão de copiar. Um link inventado seria copiado para dentro de um criativo, e o erro só
 * apareceria depois do dinheiro gasto.
 */
export function enderecoPublico(redirector: Pick<Redirector, 'publicUrl' | 'slug'>): string | null {
  const doServidor = redirector.publicUrl?.trim();
  if (doServidor) return doServidor;
  return shortLinkFor(redirector.slug);
}

/* ========================================================================== */
/*  Diagnóstico de origem — o maior valor desta tela                           */
/* ========================================================================== */

/**
 * Os quatro desfechos possíveis de "este link está recebendo origem?".
 *
 * `SEM_CLIQUES` existe separado de `NADA_COM_ORIGEM` por causa da invariante 8: um link
 * recém-criado **não** está com "0% de origem" — ele não tem medição nenhuma. Tratar os dois
 * como o mesmo caso faria a tela acusar instalação errada em todo link novo, e um alarme que
 * dispara sozinho no primeiro minuto é um alarme que ninguém lê no dia em que importa.
 */
export type OriginDiagnosis =
  | { kind: 'SEM_CLIQUES' }
  | { kind: 'TUDO_COM_ORIGEM'; total: number }
  | { kind: 'PARTE_SEM_ORIGEM'; total: number; noOrigin: number }
  | { kind: 'NADA_COM_ORIGEM'; total: number; noOrigin: number };

export type DiagnosisInput = Pick<Redirector, 'clicksTotal' | 'clicksNoOrigin' | 'lastClickAt'>;

/**
 * Classifica o link. **Comparação, não conta.**
 *
 * `lastClickAt === null` é a fonte da verdade de "nunca aconteceu" — é o campo que o serviço
 * deixa nulo quando o `left join` não achou clique nenhum, enquanto `clicksTotal` é
 * normalizado para `0` lá mesmo. Os dois são conferidos aqui porque discordar deles é sinal
 * de contrato mudando, e nesse caso o lado seguro é "sem dados".
 */
export function diagnoseOrigin(redirector: DiagnosisInput): OriginDiagnosis {
  const total = redirector.clicksTotal;
  const noOrigin = redirector.clicksNoOrigin;

  if (redirector.lastClickAt === null || total <= 0) return { kind: 'SEM_CLIQUES' };
  if (noOrigin <= 0) return { kind: 'TUDO_COM_ORIGEM', total };
  if (noOrigin >= total) return { kind: 'NADA_COM_ORIGEM', total, noOrigin };
  return { kind: 'PARTE_SEM_ORIGEM', total, noOrigin };
}

export interface DiagnosisSpec {
  /** Rótulo curto, para badge de linha. */
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  /** A frase de uma linha, com os números do servidor — sem porcentagem. */
  summary: (d: OriginDiagnosis) => string;
}

/**
 * Os textos.
 *
 * Reparar que `SEM_CLIQUES` fala de EXPECTATIVA ("ainda não recebeu") e os outros três falam
 * de MEDIÇÃO. É a diferença entre "não sei" e "sei, e é zero" dita em português, que é onde
 * a invariante 8 encosta no usuário.
 */
export const DIAGNOSIS: Record<OriginDiagnosis['kind'], DiagnosisSpec> = {
  SEM_CLIQUES: {
    label: 'Ainda sem cliques',
    tone: 'neutral',
    summary: () =>
      'Este link ainda não recebeu nenhum clique, então não dá para dizer nada sobre a origem dele.',
  },
  TUDO_COM_ORIGEM: {
    label: 'Origem chegando',
    tone: 'success',
    summary: (d) =>
      d.kind === 'TUDO_COM_ORIGEM'
        ? `Todos os ${contagem(d.total, 'clique', 'cliques')} chegaram com origem. A instalação da página está certa.`
        : '',
  },
  PARTE_SEM_ORIGEM: {
    label: 'Parte sem origem',
    tone: 'warning',
    summary: (d) =>
      d.kind === 'PARTE_SEM_ORIGEM'
        ? `De ${contagem(d.total, 'clique', 'cliques')}, ${d.noOrigin} chegaram sem nenhum parâmetro de origem.`
        : '',
  },
  NADA_COM_ORIGEM: {
    label: 'Nenhum com origem',
    tone: 'danger',
    summary: (d) =>
      d.kind === 'NADA_COM_ORIGEM'
        ? `Os ${contagem(d.total, 'clique', 'cliques')} deste link chegaram sem nenhum parâmetro de origem.`
        : '',
  },
};

/** `true` quando existe clique sem origem — o gatilho do aviso e da instrução de conserto. */
export function precisaDeConserto(diagnosis: OriginDiagnosis): boolean {
  return diagnosis.kind === 'PARTE_SEM_ORIGEM' || diagnosis.kind === 'NADA_COM_ORIGEM';
}

function contagem(valor: number, singular: string, plural: string): string {
  return `${valor} ${valor === 1 ? singular : plural}`;
}

/**
 * QUANDO um clique é gravado como `NO_ORIGIN` — conferido em
 * `apps/api/src/modules/redirector/click-params.ts` (`originOf` + `clean` + `asMetaId`), não
 * suposto. É o texto que a tela mostra, e um diagnóstico errado é pior do que nenhum.
 */
export const QUANDO_E_SEM_ORIGEM =
  'O clique é marcado como “sem origem” quando o endereço que chega até nós não traz nada que diga de onde ele veio: ' +
  'nem fbclid, nem id numérico da Meta (meta_campaign_id, fb_ad_id ou um utm_campaign/utm_term/utm_content com 6 dígitos ou mais), ' +
  'nem nenhum utm_. Parâmetro vazio não conta, e macro não substituída — o literal {{campaign.id}}, que é o que chega quando o link ' +
  'é usado fora de um anúncio — é descartada de propósito, então também cai aqui.';

/** As causas prováveis, em ordem de frequência. Todas saem do ADR-0013. */
export const CAUSAS_SEM_ORIGEM: readonly { titulo: string; texto: string }[] = [
  {
    titulo: 'O botão da sua página usa um link fixo',
    texto:
      'É a causa mais comum, e a única que se conserta em dois minutos: o botão precisa carregar a query da própria página para o endereço do nosso link. O trecho pronto está logo abaixo.',
  },
  {
    titulo: 'Tem um encurtador no meio do caminho',
    texto:
      'Se o botão aponta para um encurtador que não repassa parâmetros, o efeito é o mesmo do link fixo. Aponte o botão direto para o nosso endereço.',
  },
  {
    titulo: 'O link foi usado fora de um anúncio',
    texto:
      'Colado num story, num grupo ou aberto no navegador para testar, ele chega sem origem mesmo — e isso está certo. Um punhado de cliques assim é normal; a maioria deles não é.',
  },
  {
    titulo: 'O pixel da Meta no botão não substitui o repasse',
    texto:
      'O pixel conta o clique para a Meta; ele não manda nada para nós. São dois registros do mesmo clique, em lados diferentes — e é justamente compará-los que o painel faz.',
  },
];

/* ========================================================================== */
/*  Origem de um clique                                                        */
/* ========================================================================== */

export type ClickOrigin = RedirectorClick['origin'];

export interface OriginSpec {
  label: string;
  tone: 'success' | 'info' | 'warning';
  hint: string;
}

/**
 * `TAGGED` não é um erro, e é por isso que ele existe separado de `NO_ORIGIN`: tráfego
 * marcado à mão (orgânico, e-mail, outro canal) é instalação CERTA de um caso diferente.
 * Tratá-lo como problema mandaria o cliente consertar o que já está certo.
 */
export const CLICK_ORIGIN: Record<ClickOrigin, OriginSpec> = {
  META_ADS: {
    label: 'Meta Ads',
    tone: 'success',
    hint: 'Veio de um anúncio: traz fbclid ou um id da Meta. É a fatia que dá para cruzar com o que foi gasto.',
  },
  TAGGED: {
    label: 'Marcado',
    tone: 'info',
    hint: 'Traz utm_, sem identificador da Meta. Tráfego marcado à mão: orgânico, e-mail, outro canal.',
  },
  NO_ORIGIN: {
    label: 'Sem origem',
    tone: 'warning',
    hint: 'Chegou sem nenhum parâmetro. Este clique não pode ser creditado a nenhuma campanha.',
  },
};

/* ========================================================================== */
/*  O que aconteceu com o clique                                               */
/* ========================================================================== */

export type ClickStatus = RedirectorClick['status'];

export interface ClickStatusSpec {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  hint: string;
}

/**
 * `BLOCKED` é neutro de propósito: link ou campanha pausada é **decisão de gente**, não falha.
 * Pintá-lo de vermelho ensinaria a ignorar o vermelho — e `NO_GROUP_AVAILABLE`, que é o que
 * custa entrada de verdade, ficaria no meio do ruído.
 */
export const CLICK_STATUS: Record<ClickStatus, ClickStatusSpec> = {
  REDIRECTED: {
    /*
     * "Mandado ao grupo", e NÃO "Entrou no grupo": o redirect só prova que a pessoa recebeu o
     * convite. Se ela entrou é outra pergunta, respondida pela aba "Quem clicou" a partir da
     * entrada casada (invariante 12 — ausência de resposta não vale como resposta).
     */
    label: 'Mandado ao grupo',
    tone: 'success',
    hint: 'A pessoa foi mandada para o convite de um grupo com vaga. Se ela entrou, a aba “Quem clicou” mostra.',
  },
  NO_GROUP_AVAILABLE: {
    label: 'Sem grupo com vaga',
    tone: 'danger',
    hint: 'Nenhum grupo da campanha podia receber este clique. Se houver destino de reserva, a pessoa foi para lá.',
  },
  BLOCKED: {
    label: 'Bloqueado',
    tone: 'neutral',
    hint: 'O link ou a campanha estava pausado. Foi alguém que decidiu isso, não é falha.',
  },
  ERROR: {
    label: 'Erro',
    tone: 'danger',
    hint: 'A vaga foi reservada e o convite do grupo não servia como endereço. O motivo da linha diz qual grupo.',
  },
};

/* ========================================================================== */
/*  Situação do link                                                           */
/* ========================================================================== */

export interface RedirectorStateSpec {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
  hint: string;
}

/**
 * O link tem três situações e elas se leem na MESMA ordem da campanha: distribuindo, pausado,
 * arquivado. Arquivar aqui não é só esconder — o servidor grava `BLOCKED` em cada clique que
 * chegar depois, e a frase precisa dizer isso.
 */
export function redirectorState(
  redirector: Pick<Redirector, 'isActive' | 'archivedAt' | 'strategy'>,
): RedirectorStateSpec {
  if (redirector.archivedAt !== null) {
    return {
      label: 'Arquivado',
      tone: 'neutral',
      hint: 'Fora da operação. Quem clicar no endereço não vai para grupo nenhum — mas o clique continua sendo registrado.',
    };
  }
  if (!redirector.isActive) {
    return {
      label: 'Pausado',
      tone: 'warning',
      hint: 'Para de mandar gente para os grupos. O endereço continua de pé e o clique continua sendo registrado.',
    };
  }
  /*
   * A frase do "distribuindo" era FIXA ("cada clique vai para o próximo grupo que ainda tem
   * vaga") e descrevia o `FILL_SEQUENTIAL` como se fosse a regra de todo link. Num link de
   * sorteio ou de grupo fixo ela passou a ser falsa — e texto fixo fingindo saber o estado do
   * servidor vira mentira permanente, em toda tela que reusa este vocabulário.
   */
  return {
    label: 'Distribuindo',
    tone: 'success',
    hint: `No ar. ${REDIRECTOR_STRATEGY[redirector.strategy].consequence}`,
  };
}

/** Janela de atribuição em texto. Só EXIBE o que o servidor já guardou. */
export function janelaEmTexto(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  if (minutos % 60 === 0) {
    const horas = minutos / 60;
    return horas === 1 ? '1 hora' : `${horas} horas`;
  }
  return `${Math.floor(minutos / 60)}h${String(minutos % 60).padStart(2, '0')}`;
}

/* ========================================================================== */
/*  Estratégia de distribuição — a escolha, com a consequência escrita         */
/* ========================================================================== */

export type RedirectorStrategy = Redirector['strategy'];

/**
 * As três estratégias na ordem em que se explicam, e **não** na ordem do enum.
 *
 * `FILL_SEQUENTIAL` primeiro porque é o padrão e o comportamento que todo link já tinha;
 * `RANDOM` depois, porque é a variação do mesmo gesto (distribuir); `SPECIFIC_GROUP` por
 * último, porque é o único que **deixa de distribuir** — e quem lê nessa ordem entende a
 * troca antes de escolher.
 */
export const STRATEGY_ORDER: readonly RedirectorStrategy[] = [
  'FILL_SEQUENTIAL',
  'RANDOM',
  'SPECIFIC_GROUP',
];

export interface StrategySpec {
  label: string;
  /**
   * **O QUE ACONTECE** se você escolher esta. Escolha sem consequência à vista é chute: o
   * nome sozinho ("sequencial", "aleatório") não diz a ninguém o que muda no dia seguinte.
   */
  consequence: string;
  /** O preço da escolha. Toda uma delas tem um, e esconder o preço é vender promessa. */
  tradeoff: string;
}

/**
 * Traduzido do comentário de `redirectorStrategy` em `packages/db/src/schema/redirectors.ts`
 * — **traduzido, não copiado**: lá está escrito para quem lê SQL ("enche até o limite de
 * segurança", "elegíveis"), aqui para quem paga o anúncio.
 *
 * O que NÃO se perde na tradução é o par: cada estratégia ganha o que ela entrega **e** o
 * que ela cobra. Uma lista de três nomes bonitos faria a pessoa escolher o do meio.
 */
export const REDIRECTOR_STRATEGY: Record<RedirectorStrategy, StrategySpec> = {
  FILL_SEQUENTIAL: {
    label: 'Encher um grupo de cada vez',
    consequence:
      'Todo mundo entra no mesmo grupo até ele bater o limite de segurança. Só então o link começa a encher o próximo da fila.',
    tradeoff:
      'Grupo cheio conversa mais e parece mais forte para quem chega. Em troca, o risco fica concentrado: se esse grupo cair, cai o dia inteiro de anúncio.',
  },
  RANDOM: {
    label: 'Sortear entre os grupos',
    consequence:
      'Cada clique cai num grupo sorteado na hora, entre os que ainda têm vaga. Não dá para dizer de antemão para qual.',
    tradeoff:
      'Um banimento leva uma fatia, e não tudo. Em troca, todo grupo cresce devagar — e grupo vazio converte pior no começo.',
  },
  SPECIFIC_GROUP: {
    label: 'Mandar sempre para um grupo fixo',
    consequence:
      'Todo clique deste link vai para o grupo que você escolher, e para mais nenhum. É o que permite dois anúncios da mesma campanha irem para grupos diferentes e serem comparados.',
    tradeoff:
      'Quando esse grupo enche, o link NÃO desvia para outro: quem clicar vai para o destino de reserva e o painel abre um incidente. É de propósito — desviar faria a comparação mentir.',
  },
};

/**
 * O aviso que precisa estar na tela **antes** de alguém escolher o grupo fixo.
 *
 * É o mesmo fato do `tradeoff`, dito na hora da escolha e não num parágrafo de ajuda: o
 * comportamento "não desvia" é deliberado, e quem descobre depois o lê como bug.
 */
export const GRUPO_FIXO_QUANDO_ENCHE =
  'Se este grupo encher, o link não procura outro: quem clicar vai para o destino de reserva e o painel registra um incidente. É assim de propósito — desviar faria a comparação entre grupos mentir.';

/* ========================================================================== */
/*  A prévia honesta: "para onde vai o próximo clique?"                        */
/* ========================================================================== */

/**
 * A frase que abre a prévia, **uma por estratégia**.
 *
 * Era uma só, e valia enquanto `FILL_SEQUENTIAL` era a única: "o próximo vai para X". Com
 * sorteio ligado essa frase vira mentira — o painel apontaria o grupo 1 e o clique cairia no
 * grupo 4, sem erro e sem log. Ver `NextTargetRow.isNext` no serviço: quantos vêm `true`
 * depende da estratégia.
 */
export const PREVIEW_HEADLINE: Record<RedirectorStrategy, string> = {
  FILL_SEQUENTIAL:
    'O próximo clique vai para um grupo: o primeiro da fila que ainda tem vaga. Os outros só começam a receber quando ele encher.',
  SPECIFIC_GROUP:
    'Só o fixado recebe o próximo clique. Os outros grupos da campanha aparecem aqui para você conferir, mas não recebem nada por este link.',
  RANDOM:
    'O próximo clique pode cair em qualquer um dos que cabem: o sorteio acontece no momento do clique, então não dá para apontar um.',
};

/** O rótulo da linha que PODE receber o próximo clique — também muda com a estratégia. */
export const PREVIEW_NEXT_LABEL: Record<RedirectorStrategy, string> = {
  FILL_SEQUENTIAL: 'Próximo',
  SPECIFIC_GROUP: 'Sempre este',
  RANDOM: 'Pode ser este',
};

export interface TargetFacts {
  isNext: boolean;
  isPinned: boolean;
  strategy: RedirectorStrategy;
  headroom: number | null;
}

export interface TargetVerdict {
  label: string;
  tone: 'accent' | 'neutral' | 'warning';
  hint: string;
  /** `true` quando a linha é uma das que podem receber — o único destaque da lista. */
  destaque: boolean;
}

/**
 * O que dizer de UMA linha da prévia.
 *
 * Reparar no caso que não existia antes: num link de grupo fixo, os outros grupos da campanha
 * não estão "na fila" — eles **nunca** recebem por este link, e chamá-los de fila prometeria
 * um desvio que o allocator não faz.
 */
export function targetVerdict(target: TargetFacts): TargetVerdict {
  if (target.isNext) {
    return {
      label: PREVIEW_NEXT_LABEL[target.strategy],
      tone: 'accent',
      hint:
        target.strategy === 'RANDOM'
          ? 'Este grupo é um dos que podem receber o próximo clique. O sorteio é no momento do clique.'
          : target.strategy === 'SPECIFIC_GROUP'
            ? 'Todo clique deste link vem para este grupo enquanto houver vaga.'
            : 'O próximo clique vai para este grupo.',
      destaque: true,
    };
  }

  if (target.strategy === 'SPECIFIC_GROUP') {
    return target.isPinned
      ? {
          label: 'Fixado, sem vaga',
          tone: 'warning',
          hint: GRUPO_FIXO_QUANDO_ENCHE,
          destaque: false,
        }
      : {
          label: 'Não recebe por este link',
          tone: 'neutral',
          hint: 'Este link manda tudo para o grupo fixo. Este grupo só receberia se você trocasse a estratégia.',
          destaque: false,
        };
  }

  if (target.headroom === 0) {
    return {
      label: 'Sem vaga',
      tone: 'neutral',
      hint: 'Bateu o limite de segurança. Volta a receber se alguém sair ou se você subir o limite.',
      destaque: false,
    };
  }

  return {
    label: 'Na fila',
    tone: 'neutral',
    hint:
      target.strategy === 'RANDOM'
        ? 'Fora do sorteio por enquanto.'
        : 'Começa a receber quando os grupos acima encherem.',
    destaque: false,
  };
}

/* ========================================================================== */
/*  Prontidão de entrega — quantos grupos deste link recebem gente de verdade  */
/* ========================================================================== */

/**
 * **A pergunta que faltava na tela, e que custou um link inteiro.**
 *
 * Um redirecionador criado hoje mandaria 100% dos cliques para o destino de reserva, porque
 * nenhum dos grupos da campanha tinha convite — e nada na tela dizia isso. O convite é a
 * única coisa que o allocator exige e que NÃO aparece em lugar nenhum: a cláusula de
 * elegibilidade dele é `g.invite_code is not null and g.invite_revoked_at is null`
 * (`allocator.ts`), e um grupo sem convite é pulado **em silêncio**.
 *
 * Reparar que a lista de grupos da campanha (`campaign.groups`) **não traz o convite** — quem
 * traz é `group.list`. Por isso esta função recebe as duas coisas: os grupos do link e um
 * jeito de perguntar o convite de cada um. Inventar aqui um "tem convite" a partir do que a
 * campanha devolve seria exatamente o texto fixo que finge saber um estado do servidor.
 */
export type ReadinessKind =
  'PRONTO' | 'SEM_CONVITE' | 'NAO_MONITORADO' | 'PAUSADO' | 'CHEIO' | 'NAO_SABEMOS';

export interface ReadinessSpec {
  label: string;
  /** O EFEITO, não o estado: o que deixa de acontecer por causa disso. */
  efeito: string;
  tone: 'success' | 'warning' | 'neutral';
}

export const READINESS: Record<ReadinessKind, ReadinessSpec> = {
  PRONTO: {
    label: 'Pronto',
    efeito: 'Tem convite e tem vaga: pode receber quem clicar.',
    tone: 'success',
  },
  /**
   * O silencioso, e o motivo de esta seção existir. A frase diz o EFEITO ("nunca recebe
   * ninguém"), e não o estado ("invite_code nulo") — quem opera não sabe o que é um
   * invite_code, mas sabe muito bem o que é um grupo que nunca recebe gente.
   */
  SEM_CONVITE: {
    label: 'Sem convite',
    efeito:
      'Este grupo nunca recebe ninguém: sem o link de convite do WhatsApp, o redirecionador pula ele sem avisar e manda quem clicou para o destino de reserva.',
    tone: 'warning',
  },
  NAO_MONITORADO: {
    label: 'Não monitorado',
    efeito:
      'Você deixou de monitorar este grupo. Ele continua vinculado à campanha, mas não recebe mais ninguém.',
    tone: 'neutral',
  },
  PAUSADO: {
    label: 'Pausado',
    efeito: 'Fora do rodízio por escolha sua. Volta a receber quando você retomar.',
    tone: 'neutral',
  },
  CHEIO: {
    label: 'Cheio',
    efeito:
      'Bateu o limite de segurança e saiu do rodízio. Volta sozinho se alguém sair, ou se você subir o limite.',
    tone: 'neutral',
  },
  /**
   * Existe porque **não saber não é "não tem"** (invariante 8): enquanto a lista de grupos
   * não chegou, ou quando o grupo não está nela, a tela diz que não sabe em vez de acusar
   * falta de convite — um alarme falso aqui ensina a ignorar o alarme verdadeiro.
   */
  NAO_SABEMOS: {
    label: 'Ainda não sabemos',
    efeito: 'Não conseguimos conferir o convite deste grupo agora.',
    tone: 'neutral',
  },
};

/** O mínimo que a prontidão precisa saber de um grupo da campanha. */
export interface CampaignGroupFacts {
  groupId: string;
  name: string | null;
  status: 'ACTIVE' | 'FULL' | 'ARCHIVED';
  isActive: boolean;
  archivedAt: string | Date | null;
}

export interface ReadinessRow {
  groupId: string;
  name: string | null;
  kind: ReadinessKind;
}

export interface DeliveryReadiness {
  total: number;
  prontos: number;
  /** Só os que NÃO estão prontos, na ordem da campanha. */
  impedidos: readonly ReadinessRow[];
  /** Todos, na ordem da campanha — a lista que a tela mostra. */
  linhas: readonly ReadinessRow[];
  /** Quantos estão parados por falta de convite. É o número que abre o aviso. */
  semConvite: number;
  /** `true` quando NENHUM grupo pode receber. Todo clique vai para a reserva. */
  nenhumPronto: boolean;
}

/**
 * Classifica cada grupo do link. **Ordem dos motivos é decisão de produto:**
 *
 *  1. não monitorado (o grupo saiu da operação — nem dá para perguntar o convite dele);
 *  2. **sem convite**, antes de pausado e de cheio, porque é o único invisível: pausado e
 *     cheio a pessoa escolheu ou viu acontecer, e os dois voltam sozinhos;
 *  3. pausado, depois cheio.
 *
 * `conviteDe` devolve `undefined` quando não sabemos (lista ainda carregando, ou grupo fora
 * dela), e aí a linha vira `NAO_SABEMOS` — nunca `SEM_CONVITE`.
 */
export function avaliarEntrega(
  grupos: readonly CampaignGroupFacts[],
  conviteDe: (groupId: string) => string | null | undefined,
): DeliveryReadiness {
  const linhas = grupos.map((grupo): ReadinessRow => {
    const base = { groupId: grupo.groupId, name: grupo.name };
    if (grupo.archivedAt !== null || grupo.status === 'ARCHIVED') {
      return { ...base, kind: 'NAO_MONITORADO' };
    }

    const convite = conviteDe(grupo.groupId);
    if (convite === undefined) return { ...base, kind: 'NAO_SABEMOS' };
    if (convite === null || convite.length === 0) return { ...base, kind: 'SEM_CONVITE' };

    if (!grupo.isActive) return { ...base, kind: 'PAUSADO' };
    if (grupo.status === 'FULL') return { ...base, kind: 'CHEIO' };
    return { ...base, kind: 'PRONTO' };
  });

  const impedidos = linhas.filter((linha) => linha.kind !== 'PRONTO');
  const prontos = linhas.length - impedidos.length;

  return {
    total: linhas.length,
    prontos,
    impedidos,
    linhas,
    semConvite: linhas.filter((linha) => linha.kind === 'SEM_CONVITE').length,
    nenhumPronto: linhas.length > 0 && prontos === 0,
  };
}

/**
 * A frase de uma linha: **quantos dos grupos deste link estão prontos**.
 *
 * Sem porcentagem e sem número inventado: "2 de 3" são duas contagens que a própria lista
 * devolve. `total === 0` é um caso à parte — campanha sem grupo nenhum não é "0 de 0
 * prontos", é um link que não tem para onde mandar.
 */
export function resumoDaEntrega(r: DeliveryReadiness): string {
  if (r.total === 0) {
    return 'A campanha deste link não tem nenhum grupo vinculado — não há para onde mandar quem clicar.';
  }
  if (r.nenhumPronto) {
    return `Nenhum dos ${r.total} ${r.total === 1 ? 'grupo' : 'grupos'} deste link pode receber gente agora.`;
  }
  if (r.prontos === r.total) {
    return `Os ${r.total} ${r.total === 1 ? 'grupo está pronto' : 'grupos estão prontos'} para receber gente.`;
  }
  return `${r.prontos} de ${r.total} grupos deste link estão prontos para receber gente.`;
}

/**
 * O que acontece com quem clicar quando nenhum grupo pode receber — e isso depende do
 * destino de reserva, que é dado do servidor. Sem ele o visitante vê uma página de
 * indisponibilidade; **nunca** um redirect adivinhado (invariante 7).
 */
export function efeitoDeNenhumPronto(temFallback: boolean): string {
  return temFallback
    ? 'Enquanto for assim, 100% dos cliques deste link vão para o destino de reserva — ninguém entra em grupo nenhum.'
    : 'Enquanto for assim, quem clicar vê uma página de indisponível — ninguém entra em grupo nenhum. O clique continua sendo registrado.';
}

/* ========================================================================== */
/*  B6 — quando nenhum grupo pode receber                                      */
/* ========================================================================== */

export type NoGroupAction = Redirector['noGroupAction'];

/** A ordem das opções no formulário. O padrão (o de sempre) vem primeiro. */
export const NO_GROUP_ACTION_ORDER: readonly NoGroupAction[] = ['FALLBACK_URL', 'CONTINGENCY_GROUP'];

/** Cada opção com a CONSEQUÊNCIA escrita — mesma regra da escolha de estratégia. */
export const NO_GROUP_ACTION: Record<NoGroupAction, { label: string; consequence: string }> = {
  FALLBACK_URL: {
    label: 'Mandar para o destino de reserva',
    consequence:
      'Quando o convite quebra ou todos os grupos enchem, quem clicar vai para o destino de reserva (ou vê a página de link indisponível, se não houver).',
  },
  CONTINGENCY_GROUP: {
    label: 'Mandar para um grupo de contingência',
    consequence:
      'Quando o convite quebra ou todos os grupos enchem, quem clicar entra num grupo de reserva que você escolhe. Se nem ele tiver vaga, vale o destino de reserva.',
  },
};

/**
 * A frase da gaveta sobre o que acontece sem grupo. Não decide nada: só traduz a
 * configuração que veio do servidor.
 */
export function destinoSemGrupo(
  redirector: Pick<
    Redirector,
    'noGroupAction' | 'contingencyGroupName' | 'fallbackLabel' | 'fallbackUrl'
  >,
): string {
  const reserva =
    redirector.fallbackUrl === null
      ? 'a página de link indisponível'
      : `o destino de reserva “${redirector.fallbackLabel ?? redirector.fallbackUrl}”`;
  if (redirector.noGroupAction === 'CONTINGENCY_GROUP') {
    const grupo = redirector.contingencyGroupName ?? 'sem nome no WhatsApp';
    return `Quando nenhum grupo da campanha puder receber (convite quebrado ou lotação), quem clicar entra no grupo de contingência “${grupo}”. Se nem ele tiver vaga, vai para ${reserva}. Pausar o link não usa a contingência.`;
  }
  return `Quando nenhum grupo da campanha puder receber (convite quebrado ou lotação), quem clicar vai para ${reserva}. O clique é registrado do mesmo jeito.`;
}

/* ========================================================================== */
/*  A1 — prévias de robô (não são clique)                                      */
/* ========================================================================== */

/**
 * "38 prévias do WhatsApp/Facebook (não contam como clique)".
 *
 * Só JUNTA o que veio pronto (total, plataformas e rótulos do servidor) — nenhuma soma aqui.
 * `0` é zero contado e tem frase própria; quando algum dia bateu o teto, o número vira
 * "pelo menos N", porque passar do teto não é contado.
 */
export function previasEmTexto(resumo: {
  total: number;
  capped: boolean;
  byKind: ReadonlyArray<{ label: string }>;
}): string {
  if (resumo.total === 0) return 'Nenhuma prévia de link gerada neste período.';
  const plataformas = resumo.byKind.map((row) => row.label).join(', ');
  const numero = resumo.capped ? `Pelo menos ${resumo.total}` : String(resumo.total);
  const palavra = resumo.total === 1 ? 'prévia' : 'prévias';
  return `${numero} ${palavra} de ${plataformas} (não contam como clique).`;
}

/** Por que a prévia existe — o tooltip/legenda da linha acima. */
export const POR_QUE_PREVIAS =
  'Quando alguém cola o link no WhatsApp ou no Facebook, o aplicativo busca o título e a imagem para montar o cartão. Essa busca não é uma pessoa clicando: não conta como clique, não reserva vaga e não entra na taxa de entrada.';
